<template>
  <form @submit.prevent="submit" :class="errorClass">
    <rp-select
      v-if="!userIsSurgeon && operationIsNew"
      v-model="resource.surgeon_id"
      placeholder="Surgeon"
      @input="touch('surgeon_id')"
      :error="surgeon_idError">
      <option v-for="surgeon in surgeons" :key="surgeon.id" :value="surgeon.id">
        {{surgeon.profile.first_name}} {{surgeon.profile.last_name}}
      </option>
    </rp-select>

    <rp-select
      v-if="resource.surgeon_id"
      v-model="resource.hospital_id"
      placeholder="Hospital"
      isRequired
      @input="touch('hospital_id')"
      :error="hospital_idError">
      <option v-for="hospital in hospitals" :key="hospital.id" :value="hospital.id">{{hospital.name}}</option>
    </rp-select>

    <rp-datepicker
      v-model="resource.performed_at"
      placeholder="Surgery Date"
      isRequired
      @input="touch('performed_at')"
      :error="performed_atError"/>

    <rp-checkbox
      v-model="resource.blended_vision"
      @input="touch('blended_vision')"
      label="Monovision"/>

    <rp-checkbox
      v-model="resource.complex_surgery"
      @input="touch('complex_surgery')"
      label="Complex Surgery"/>

    <rp-checkbox
      v-model="resource.aeon_drops_provided"
      @input="touch('aeon_drops_provided')"
      label="AEON Repair eye drops provided"/>

    <div class="item">
      <h3>Operated eyes</h3>
      <div class="asterisk">
        *
      </div>
    </div>

    <span>
      <rp-checkbox
        v-model="resource.leftEye"
        :disabled="this.resource.surgeon_id === ''"
        @input="touch('leftEye')"
        label="Left Eye"/>

      <span v-if="resource.leftEye">
        <a :href="'mailto:raypro@rayner.com subject=RayPRO IOL Request'"
          class="contact-btn">
          Can't find the right IOL? Click here to request it!
        </a>
        <rp-iol-model-selector
          v-model="resource"
          :iolModels="iolModels"
          :powers="leftIolPowers"
          :cylPowers="leftCylPowers"
          modelPlaceholder="Left IOL Model"
          powerPlaceholder="Left IOL Power"
          powerTouch="iol_power_left"
          cylPlaceholder="Left IOL Power (Cyl)"
          cylTouch="iol_power_left_cyl"
          resourceObjectModel="iol_model_left"
          resourceObjectPower="iol_power_left"
          resourceObjectCyl="iol_power_left_cyl"
          resourceObjectEye="leftEye"
          @model="leftIolTouch"
          @childDropdownInput="touchSelector"
        />
        <rp-iol-model-selector
          v-model="resource"
          :iolModels="secondarySulcoflexDuetModels"
          :powers="leftDuetIolPowers"
          :cylPowers="leftDuetCylPowers"
          v-if="resource.left_duet"
          modelPlaceholder="DUET Left IOL Model"
          powerPlaceholder="DUET Left IOL Power"
          powerTouch="iol_power_left_duet"
          cylPlaceholder="DUET Left IOL Power (Cyl)"
          cylTouch="iol_power_left_cyl_duet"
          resourceObjectModel="iol_model_left_duet"
          resourceObjectPower="iol_power_left_duet"
          resourceObjectCyl="iol_power_left_cyl_duet"
          resourceObjectEye="leftEye"
          @model="leftDuetIolTouch"
          @childDropdownInput="touchSelector"
        />
      </span>
    </span>

    <span>
      <rp-checkbox
        v-model="resource.rightEye"
        :disabled="this.resource.surgeon_id === ''"
        @input="touch('rightEye')"
        label="Right eye"/>

      <span v-if="resource.rightEye">
        <a :href="'mailto:raypro@rayner.com subject=RayPRO IOL Request'"
          class="contact-btn">
          Can't find the right IOL? Click here to request it!
        </a>
        <rp-iol-model-selector
          v-model="resource"
          :iolModels="iolModels"
          :powers="rightIolPowers"
          :cylPowers="rightCylPowers"
          modelPlaceholder="Right IOL Model"
          powerPlaceholder="Right IOL Power"
          powerTouch="iol_power_right"
          cylPlaceholder="Right IOL Power (Cyl)"
          cylTouch="iol_power_right_cyl"
          resourceObjectModel="iol_model_right"
          resourceObjectPower="iol_power_right"
          resourceObjectCyl="iol_power_right_cyl"
          resourceObjectEye="rightEye"
          @model="rightIolTouch"
          @childDropdownInput="touchSelector"
        />

        <rp-iol-model-selector
          v-model="resource"
          :iolModels="secondarySulcoflexDuetModels"
          :powers="rightDuetIolPowers"
          :cylPowers="rightDuetCylPowers"
          v-if="resource.right_duet"
          modelPlaceholder="DUET Right IOL Model"
          powerPlaceholder="DUET Right IOL Power"
          cylPlaceholder="DUET Right IOL Power (Cyl)"
          powerTouch="iol_power_right_duet"
          cylTouch="iol_power_right_cyl_duet"
          resourceObjectModel="iol_model_right_duet"
          resourceObjectPower="iol_power_right_duet"
          resourceObjectCyl="iol_power_right_cyl_duet"
          resourceObjectEye="rightEye"
          @model="rightDuetIolTouch"
          @childDropdownInput="touchSelector"
        />
      </span>
    </span>

    <span v-if="operationIsNew">
      <div class="item">
        <h3>Questionnaires</h3>
        <div class="asterisk text">
          *at least one
        </div>
      </div>

      <rp-checkbox
        v-for="(item, index) in questionnaires"
        :key="index"
        v-model="item.isSelected"
        :label="pipeQuestionnaireLabel(item.description)"
      />

      <rp-checkbox
        v-model="resource.welcome_email"
        label="Send Welcome E-Mail (if applicable)"
      />
    </span>

    <rp-button :disabled="isInvalid" type="primary">Confirm</rp-button>
  </form>
</template>

<script>
  import {validationMixin} from 'vuelidate'
  import {email, required, requiredIf, requiredUnless} from 'vuelidate/lib/validators'
  import {date} from '../../../api/validators'
  import {errorMessages} from '../../../api/error_messages'
  import {getQuestionnaires} from '../../../api/questionnaire'

  import form from '../../../mixins/form'

  import Button from '../../../components/ui/inputs/Button'
  import TextInput from '../../../components/ui/inputs/TextInput'
  import Select from '../../../components/ui/inputs/StyledSelect'
  import DatePicker from '../../../components/ui/inputs/DatePicker'
  import Checkbox from '../../../components/ui/inputs/Checkbox'
  import IolModelSelector from './IolModelSelector'

  const DEFAULT_IOL_POWER = 'Not Specified'

  export default {
    name: 'OperationForm',
    mixins: [validationMixin, form],

    components: {
      'rp-button': Button,
      'rp-textinput': TextInput,
      'rp-select': Select,
      'rp-datepicker': DatePicker,
      'rp-checkbox': Checkbox,
      'rp-iol-model-selector': IolModelSelector
    },

    data() {
      return {
        userIsSurgeon: false,
        userIsNurse: false,
        secondarySulcoflexDuetModels: [],
        questionnaires: [],
        questionnaire_answers: [],
      }
    },

    props: {
      hospitals: Array,
      surgeons: Array,
      iolModels: Array,
      leftIolPowers: Array,
      leftDuetIolPowers: Array,
      rightIolPowers: Array,
      rightDuetIolPowers: Array,
      leftCylPowers: Array,
      leftDuetCylPowers: Array,
      rightCylPowers: Array,
      rightDuetCylPowers: Array,
      operationIsNew: Boolean
    },

    mounted() {
      getQuestionnaires().then((res) => {
        const result = res.data
        this.questionnaires = result.map(obj => ({...obj, isSelected: 'true'}))
      })

      this.checkUserRoles()
      this.getIolModels()
      if(this.resource.surgeon_id) {
        this.$emit('getHospitals', this.resource.surgeon_id)
      }
    
      if(!this.userIsSurgeon) {
        this.getSurgeons()
      }

      if(this.operationIsNew) {
        this.resource.performed_at = new Date().toISOString().split('T')[0]
      }
    },

    watch: {
      questionnaires: {
        handler: function (val) {
          this.questionnaire_answers = []
          this.questionnaires.forEach((element) => {
            const isSelected = element.isSelected
            
            if(isSelected) {
              this.questionnaire_answers.push(element.id)
            }
          })

          this.$v.resource.questionnaire_answers.$touch()
          this.$emit('getQuestionnaires', this.questionnaire_answers)
        },
        deep: true
      },

      'resource.surgeon_id'(val) {
        this.checkUserRoles()
        this.getIolModels()
        if (val) {
          this.$emit('getHospitals', val)
        }
        if (this.operationIsNew && this.userIsNurse) {
          this.resource.leftEye = false
          this.resource.rightEye = false
        }
      },
      'resource.leftEye'(val) {
        if (!val) {
          this.resetLeft()
        } else {
          this.initLeft()
        }
      },
      'resource.rightEye'(val) {
        if (!val) {
          this.resetRight()
        } else {
          this.initRight()
        }
      },
      'resource.iol_model_left'(val) {
        if (val) {
          this.getIolPowers('left', this.resource.iol_model_left)
        }
      },
      'resource.iol_model_right'(val) {
        if (val) {
          this.getIolPowers('right', this.resource.iol_model_right)
        }
      },
    },

    computed: {
      ...errorMessages([
        'surgeon_id',
        'performed_at',
        'nurse_id',
        'hospital_id',
        'leftEye',
        'iol_model_left',
        'iol_power_left',
        'iol_power_left_cyl',
        'rightEye',
        'iol_model_right',
        'iol_power_right',
        'iol_power_right_cyl',
        'aeon_drops_provided',
        'complex_surgery',
        'blended_vision',
      ]),
    },

    validations: {
      resource: {
        surgeon_id: {required},
        performed_at: {required,date},
        hospital_id: {required},
        blended_vision: {required: false},
        complex_surgery: {required: false},
        aeon_drops_provided: {required: false},
        leftEye: {
          leftOrRightValid(leftEye, resource) {
            return resource.rightEye || leftEye
          }
        },
        left_duet: {required: false},
        right_duet: {required: false},
        iol_model_left: {
          required: requiredIf(function({leftEye}) {
            return leftEye
          })
        },
        iol_power_left: {
          required: requiredIf(function({leftEye}) {
            return leftEye
          })
        },
        iol_power_left_cyl: {
          required: requiredIf(function() {
            return this.leftCylPowers.length > 0
          })
        },
        rightEye: {
          leftOrRightValid(rightEye, resource) {
            return resource.leftEye || rightEye
          }
        },
        iol_model_right: {
          required: requiredIf(function({rightEye}) {
            return rightEye
          })
        },
        iol_power_right: {
          required: requiredIf(function({rightEye}) {
            return rightEye
          })
        },
        iol_power_left_duet: {
          required: requiredIf(function({left_duet, iol_model_left}) {
            return left_duet
          })
        },
        iol_power_left_cyl_duet: {
          required: requiredIf(function({left_duet, iol_model_left}) {
            return left_duet && this.leftDuetCylPowers.length > 0
          })
        },
        iol_model_left_duet: {
          required: requiredIf(function({left_duet, iol_model_left}) {
            return this.resource.left_duet
          })
        },
        iol_model_right_duet: {
          required: requiredIf(function({right_duet, iol_model_right}) {
            return right_duet
          })
        },
        iol_power_right_duet: {
          required: requiredIf(function({right_duet, iol_model_right}) {
            return right_duet
          })
        },
        iol_power_right_cyl_duet: {
          required: requiredIf(function({right_duet, iol_model_right}) {
            return right_duet && this.rightDuetCylPowers.length > 0
          })
        },
        iol_power_right_cyl: {
          required: requiredIf(function() {
            return this.rightCylPowers.length > 0
          })
        },
        questionnaire_answers: {
          notEmpty() {
            if(this.questionnaire_answers.length > 0) {
              return true
            } else {
              return false
            }
          }
        },
      },
    },

    methods: {
      pipeQuestionnaireLabel(label) {
        let result = label

        if(result.startsWith('Questionnaire')) {
          result = result.substring(14)
          result = result.charAt(0).toUpperCase() + result.slice(1)
        }

        return result
      },

      initLeft() {
        if (this.resource.iol_model_left) {
          const mainIolId = this.resource.iol_model_left;
          if (this.isModelNameContains(mainIolId, 'duet')) {
            this.setDuetSecondaryModels();
            this.setDuetModelState('left', true);
          }
        }
        if (this.resource.iol_model_left_duet) {
          const duetIolId = this.resource.iol_model_left_duet;
          this.getIolPowers('leftDUET', duetIolId);
        }
      },

      resetLeft() {
        this.resource.left_duet = false
        this.resource.iol_model_left = ''
        this.resource.iol_power_left = ''
        this.resource.iol_power_left_cyl = ''
        this.resource.iol_model_left_duet = ''
        this.resource.iol_power_left_duet = ''
        this.resource.iol_power_left_cyl_duet = ''
        this.leftIolPowers.length = 0
        this.leftDuetIolPowers.length = 0
        this.leftCylPowers.length = 0
        this.leftDuetCylPowers.length = 0
      },

      initRight() {
        if (this.resource.iol_model_right) {
          const mainIolId = this.resource.iol_model_right;
          if (this.isModelNameContains(mainIolId, 'duet')) {
            this.setDuetSecondaryModels();
            this.setDuetModelState('right', true);
          }
        }
        if (this.resource.iol_model_right_duet) {
          const duetIolId = this.resource.iol_model_right_duet;
          this.getIolPowers('rightDUET', duetIolId);
        }
      },

      resetRight() {
        this.resource.right_duet = false
        this.resource.iol_model_right = ''
        this.resource.iol_power_right = ''
        this.resource.iol_power_right_cyl = ''
        this.resource.iol_model_right_duet = ''
        this.resource.iol_power_right_duet = ''
        this.resource.iol_power_right_cyl_duet = ''
        this.rightIolPowers.length = 0
        this.rightDuetIolPowers.length = 0
        this.rightCylPowers.length = 0
        this.rightDuetCylPowers.length = 0
      },

      rightIolTouch() {
        const iolModelId = this.resource.iol_model_right

        if(this.isModelNameContains(iolModelId, 'duet')) {
          this.setDuetSecondaryModels()
          this.setDuetModelState('right', true)
        } else {
          this.setDuetModelState('right', false)
        }

        this.touch('iol_power_right')
        this.touch('right_duet')
        this.touch('iol_model_right_duet')

        this.resource.iol_power_right = DEFAULT_IOL_POWER
        this.resource.iol_power_right_cyl = this.isToricIol(iolModelId) ? DEFAULT_IOL_POWER : ''
        this.getIolPowers('right', iolModelId)
      },

      leftIolTouch() {
        const iolModelId = this.resource.iol_model_left

        if(this.isModelNameContains(iolModelId, 'duet')) {
          this.setDuetSecondaryModels()
          this.setDuetModelState('left', true)
        } else {
          this.setDuetModelState('left', false)
        }
        this.touch('left_duet')
        this.touch('iol_model_left_duet')

        this.resource.iol_power_left = DEFAULT_IOL_POWER
        this.resource.iol_power_left_cyl = this.isToricIol(iolModelId) ? DEFAULT_IOL_POWER : ''
        this.getIolPowers('left', iolModelId)
      },

      leftDuetIolTouch() {
        const iolModelId = this.resource.iol_model_left_duet
        this.touch('left_duet')
        this.resource.iol_power_left_duet = DEFAULT_IOL_POWER
        this.resource.iol_power_left_cyl_duet = this.isToricIol(iolModelId) ? DEFAULT_IOL_POWER : ''
        this.getIolPowers('leftDUET', iolModelId)
      },

      rightDuetIolTouch() {
        const iolModelId = this.resource.iol_model_right_duet
        this.touch('right_duet')
        this.resource.iol_power_right_duet = DEFAULT_IOL_POWER
        this.resource.iol_power_right_cyl_duet = this.isToricIol(iolModelId) ? DEFAULT_IOL_POWER : ''
        this.getIolPowers('rightDUET', iolModelId)
      },

      isToricIol(iolId) {
        const iol = this.iolModels.find(x => x.id == iolId)
        return iol && iol.toric
      },

      touchSelector(value) {
        this.touch(value)
      },

      setDuetModelState(eye, state) {
        //If they decide to do operations on the flies than this cannot be if/else :)
        if(eye === 'left') {
          this.resource.left_duet = state

          if(!state) {
            this.resource.iol_model_left_duet = ''
            this.resource.iol_power_left_duet = ''
            this.resource.iol_power_left_cyl_duet = ''
          }

          this.touch('left_duet')
        } else {
          this.resource.right_duet = state

          if(!state) {
            this.resource.iol_model_right_duet = ''
            this.resource.iol_power_right_duet = ''
            this.resource.iol_power_right_cyl_duet = ''
          }

          this.touch('right_duet')
        }
      },

      setDuetSecondaryModels(eye) {
        this.secondarySulcoflexDuetModels = []

        this.iolModels.forEach(element => {
          if(!this.isModelNameContains(element.id, 'Sulcoflex')) {
            this.secondarySulcoflexDuetModels.push(element)
          }
        })
      },
      
      isModelNameContains(modelId, namePart) {
        const iolModelName = this.getIolModelNameById(modelId)
        let isNameContains = false

        if(iolModelName && (iolModelName.toLowerCase()).includes(namePart.toLowerCase())) {
          isNameContains = true
        }
        return isNameContains
      },

      getIolModelNameById(modelId) {
        const iolModel = this.getIolModelById(modelId)
        return (iolModel === null) ? iolModel : iolModel.model
      },

      getIolModelById(modelId) {
        let iolModel = null
        this.iolModels.forEach(element => {
          if(element.id == modelId) {
            iolModel = element
          }
        })
        return iolModel
      },

      getSurgeons() {
        this.$emit('getSurgeons')
      },

      getIolModels() {
        this.$emit('getIolModels', true)
      },

      getIolPowers(type, id) {
        let iolParams = {
          type: type,
          id: id
        }

        this.$emit('getIolPowers', iolParams)
      },

      onHospitalsRefreshed(defaultHospitalId) {
        if(this.hospitals.length > 0) {
          this.resource.hospital_id = defaultHospitalId
        }
      },

      checkUserRoles() {
        this.userIsNurse = this.checkIfUserIsRole(this.$store.getters.authUser, 'nurse')
        this.userIsSurgeon = this.checkIfUserIsRole(this.$store.getters.authUser, 'surgeon')
      },

      checkIfUserIsRole(user, roleName) {
        return user.roles.reduce((acc, curr) => {
          if(acc) return true
          return curr.name === roleName
        }, false)
      }
    }
  }
</script>
<style lang="sass" scoped>
  @import '../../../styles/base/variables'
  @import '../../../styles/helpers/mixins'

  .item
    display: flex
    .required
      fill: $color-error
      display: inline
      margin: 8px 10px

  .asterisk
    &.text
      font-size: 10px

  .contact-btn
    padding: 4px
    font-size: 13px
    border-width: 2px
    border-radius: 5px
    border-style: solid
    border-color: $color-primary
    display: flex
    justify-content: center
</style>



// WEBPACK FOOTER //
// ../raypro_ui/src/components/ui/forms/Operation.vue